import React from 'react';
import { useSelector } from 'react-redux';

import { Grid, GridCol, GridRow } from '@lux/grid';
import { Tag, TitlePreset } from '@lux/title';
import { FunctionColor } from '@lux/text';
import { TextLink } from '@lux/text-link';
import { Translate, TranslateScope } from '@pegasus/translation';
import { OpenCampaignCard, OpenCampaignCardAspectRatio } from '@browsing/campaign';
import { CardsSkeletonLoader } from '@browsing/components';
import {
    trackSelectContent,
    useTrackPageView,
    useTrackCampaignsImpression,
} from '@browsing/tracking';

import { getIsMobileScreen } from '@client/redux/modules/clientDevice/selectors';
import { getIsLoading, getCategoryTabId } from '@client/redux/modules/mylounge';
import { getTrackingData } from '@client/redux/modules/tracking/selectors';
import { MYLOUNGE_ROUTE } from '@client/config/constants';

import { useMylounge } from '@client/components/mylounge/hooks';
import { useNewDealsToday } from '@client/components/mylounge/new-deals-today';

import * as S from './index.styles';

export const NewDealsToday: React.FC = () => {
    useMylounge();
    const { newDealsCampaigns } = useNewDealsToday();
    const isLoading = useSelector(getIsLoading);
    const isMobileScreen = useSelector(getIsMobileScreen);
    const categoryTabId = useSelector(getCategoryTabId);
    const tracking = useSelector(getTrackingData);

    const hasCampaigns = newDealsCampaigns?.length > 0;

    useTrackPageView({
        page: 'new_deals',
        isLoaded: hasCampaigns,
    });

    useTrackCampaignsImpression({
        categoryTabId,
        isMobileScreen,
        visitorID: tracking?.visitorID,
    });

    const handleBackClick = () => {
        trackSelectContent('navigation', 'click', { contentId: 'back' });
        if (window) {
            window.location.assign(MYLOUNGE_ROUTE);
        }
    };

    return (
        <TranslateScope path="myLounge">
            <S.MainWrapper id="main" tabIndex={-1}>
                <Grid>
                    <GridRow>
                        <GridCol span={12}>
                            <S.BreadcrumbWrapper>
                                <S.StyledChevronLeft onClick={handleBackClick} />
                                <TextLink
                                    link
                                    color={FunctionColor.function100}
                                    onClick={handleBackClick}
                                >
                                    <TranslateScope path="newDealsToday">
                                        <Translate message="backBreadcrumb" raw />
                                    </TranslateScope>
                                </TextLink>
                            </S.BreadcrumbWrapper>
                            <S.TitleWrapper
                                preset={TitlePreset.S}
                                presetSm={TitlePreset.M}
                                tag={Tag.H1}
                            >
                                <TranslateScope path="newDealsToday">
                                    <Translate message="viewTitle" raw />
                                </TranslateScope>
                            </S.TitleWrapper>
                            <S.CampaignsWrapper>
                                {(isLoading || !hasCampaigns) && (
                                    <CardsSkeletonLoader
                                        data-testid="new-deals-today-loader"
                                        itemsCount={4}
                                        width={220}
                                        cardHeight={300}
                                    />
                                )}
                                {!isLoading && hasCampaigns && (
                                    <S.GridWrapper data-testid="new-deals-today-view">
                                        {newDealsCampaigns.map((campaign, index) => (
                                            <OpenCampaignCard
                                                key={campaign.campaignId}
                                                aspectRatio={OpenCampaignCardAspectRatio.Mobile}
                                                aspectRatioSm={OpenCampaignCardAspectRatio.Square}
                                                campaign={campaign}
                                                id={`new_deals-${campaign.campaignId}`}
                                                position={`${index + 1}|1`}
                                                trackCreativeName="new_deals"
                                                trackItemListName="new_deals"
                                            />
                                        ))}
                                    </S.GridWrapper>
                                )}
                            </S.CampaignsWrapper>
                        </GridCol>
                    </GridRow>
                </Grid>
            </S.MainWrapper>
        </TranslateScope>
    );
};
